/**
 * UserActivityModal - 사용자 활동 로그 모달
 * Users 페이지에서 개별 사용자의 활동 로그를 조회
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getUserActivityLogs,
  getActionTypeLabel,
  getCategoryLabel,
  getServiceLabel,
  getStatusInfo
} from '../services/activityLogService';

// 타겟 타입별 링크 생성 함수
const getTargetLink = (targetType, targetId, service) => {
  if (!targetType || !targetId) return null;

  const baseUrls = {
    'community': 'https://community.ilouli.com',
    'main': 'https://ilouli.com',
    'ai': 'https://ai.ilouli.com',
    'admin': 'https://admin.ilouli.com',
    'stock': 'https://stock.ilouli.com',
  };

  const baseUrl = baseUrls[service] || baseUrls['community'];

  switch (targetType) {
    case 'post':
    case 'article':
      return `${baseUrl}/posts/${targetId}`;
    case 'comment':
      return `${baseUrl}/posts?comment=${targetId}`;
    case 'user':
      return `https://admin.ilouli.com/users?id=${targetId}`;
    case 'chat':
    case 'conversation':
      return `https://ai.ilouli.com/chat/${targetId}`;
    case 'briefing':
      return `${baseUrl}/briefings/${targetId}`;
    case 'api_key':
      return `https://admin.ilouli.com/api-keys?id=${targetId}`;
    case 'board':
      return `${baseUrl}/boards/${targetId}`;
    default:
      return null;
  }
};

// 타겟 타입 한글 변환
const getTargetTypeLabel = (targetType) => {
  const labels = {
    'post': '게시글',
    'article': '글',
    'comment': '댓글',
    'user': '사용자',
    'chat': '채팅',
    'conversation': '대화',
    'briefing': '브리핑',
    'api_key': 'API 키',
    'board': '게시판',
    'file': '파일',
    'image': '이미지',
  };
  return labels[targetType] || targetType;
};

const UserActivityModal = ({ user, isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  // 필터 상태
  const [filters, setFilters] = useState({
    category: '',
    from: '',
    to: '',
    search: ''
  });

  // 활동 로그 로드
  const loadLogs = useCallback(async (page = 1) => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getUserActivityLogs(user.id, {
        page,
        limit: 20,
        category: filters.category || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        search: filters.search || undefined
      });

      if (result.success) {
        setLogs(result.logs || []);
        setSummary(result.summary || null);
        setPagination({
          page: result.pagination?.page || 1,
          totalPages: result.pagination?.totalPages || 1,
          total: result.pagination?.total || 0
        });
      }
    } catch (err) {
      console.error('Failed to load activity logs:', err);
      setError(err.message || '활동 로그를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters]);

  // 모달 열릴 때 로드
  useEffect(() => {
    if (isOpen && user?.id) {
      loadLogs(1);
    }
  }, [isOpen, user?.id, loadLogs]);

  // 필터 변경 시 리로드
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // 필터 적용
  const applyFilters = () => {
    loadLogs(1);
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({ category: '', from: '', to: '', search: '' });
    setSearchQuery('');
    setActiveCategory(null);
    // 필터 초기화 후 바로 로드
    setTimeout(() => loadLogs(1), 0);
  };

  // 카테고리 칩 클릭 핸들러 - 클릭 시 즉시 필터링
  const handleCategoryClick = (category) => {
    const newCategory = activeCategory === category ? '' : category;
    setActiveCategory(newCategory || null);
    setFilters(prev => ({ ...prev, category: newCategory }));
  };

  // 필터 변경 감지하여 자동 로드 (카테고리 칩 클릭/검색 시)
  const prevCategoryRef = React.useRef(filters.category);
  const prevSearchRef = React.useRef(filters.search);

  useEffect(() => {
    const categoryChanged = prevCategoryRef.current !== filters.category;
    const searchChanged = prevSearchRef.current !== filters.search;

    if (isOpen && user?.id && (categoryChanged || searchChanged)) {
      loadLogs(1);
    }

    prevCategoryRef.current = filters.category;
    prevSearchRef.current = filters.search;
  }, [filters.category, filters.search, isOpen, user?.id, loadLogs]);


  // 검색 실행
  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }));
  };

  // 엔터키로 검색
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 상대 시간
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return '방금 전';
    if (minutes < 60) return `${minutes}분 전`;
    if (hours < 24) return `${hours}시간 전`;
    if (days < 7) return `${days}일 전`;
    return formatDate(dateString);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1500] p-5" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-[900px] max-h-[90vh] flex flex-col shadow-2xl animate-modalSlideIn overflow-hidden max-md:max-w-full max-md:max-h-full max-md:rounded-none max-md:m-0" onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="flex justify-between items-start py-5 px-6 border-b border-gray-200">
          <div>
            <h2 className="m-0 mb-1 text-lg font-semibold text-gray-900">활동 로그</h2>
            <span className="text-[13px] text-gray-600">
              {user?.nickname || user?.name} ({user?.email})
            </span>
          </div>
          <button className="bg-transparent border-none text-2xl text-gray-600 cursor-pointer p-0 leading-none transition-colors hover:text-gray-900" onClick={onClose}>&times;</button>
        </div>

        {/* 요약 통계 */}
        {summary && (
          <div className="flex flex-col gap-3 py-4 px-6 bg-slate-50 border-b border-gray-200">
            <div className="flex gap-6 max-md:flex-col max-md:gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-600">최근 30일 활동</span>
                <span className="text-[15px] font-semibold text-gray-900">{summary.total_activities || 0}회</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-600">마지막 활동</span>
                <span className="text-[15px] font-semibold text-gray-900">{getRelativeTime(summary.last_activity)}</span>
              </div>
            </div>
            {summary.by_category && Object.keys(summary.by_category).length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 font-medium">카테고리별</span>
                  <button
                    className={`text-[11px] py-1 px-3 rounded-full cursor-pointer transition-all ${!activeCategory ? 'bg-blue-500 border border-blue-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-500 hover:text-blue-500'}`}
                    onClick={() => {
                      setActiveCategory(null);
                      setFilters(prev => ({ ...prev, category: '' }));
                    }}
                  >
                    전체
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-md:w-full">
                  {Object.entries(summary.by_category).map(([cat, count]) => (
                    <button
                      key={cat}
                      className={`text-[11px] py-1 px-2.5 rounded-full cursor-pointer transition-all ${activeCategory === cat ? 'bg-blue-500 border border-blue-600 text-white' : 'bg-gray-200 text-slate-600 border border-transparent hover:bg-blue-100 hover:text-blue-800 hover:border-blue-300'}`}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {getCategoryLabel(cat)}: {count}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 필터 */}
        <div className="flex items-center gap-4 py-3 px-6 border-b border-gray-200 bg-slate-50/50 flex-wrap max-md:flex-col max-md:items-stretch max-md:gap-2.5">
          {/* 검색 행 */}
          <div className="contents max-md:flex max-md:flex-col max-md:gap-2 max-md:w-full">
            <div className="flex flex-1 min-w-[180px] max-w-[240px] max-md:max-w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="활동 내용 검색..."
                className="flex-1 py-1.5 px-3 border border-gray-200 border-r-0 rounded-l-md text-[13px] bg-white text-gray-900 focus:outline-none focus:border-blue-500 placeholder:text-slate-400"
              />
              <button className="py-1.5 px-2.5 bg-blue-500 text-white border border-blue-500 rounded-r-md cursor-pointer transition-all text-[13px] hover:bg-blue-600 hover:border-blue-600" onClick={handleSearch}>
                🔍
              </button>
            </div>
          </div>

          {/* 날짜 필터 행 */}
          <div className="contents max-md:flex max-md:flex-col max-md:gap-2 max-md:w-full">
            <div className="flex items-center gap-1.5 max-md:w-full max-md:flex-wrap">
              <span className="text-xs text-gray-600 whitespace-nowrap">기간</span>
              <input
                type="date"
                value={filters.from}
                onChange={e => handleFilterChange('from', e.target.value)}
                className="py-1.5 px-2 border border-gray-200 rounded-md text-xs bg-white text-gray-900 w-[130px] focus:outline-none focus:border-blue-500 max-md:flex-1 max-md:min-w-[120px]"
              />
              <span className="text-xs text-gray-600 whitespace-nowrap">~</span>
              <input
                type="date"
                value={filters.to}
                onChange={e => handleFilterChange('to', e.target.value)}
                className="py-1.5 px-2 border border-gray-200 rounded-md text-xs bg-white text-gray-900 w-[130px] focus:outline-none focus:border-blue-500 max-md:flex-1 max-md:min-w-[120px]"
              />
            </div>

            <div className="flex gap-1.5 ml-auto max-md:w-full max-md:ml-0">
              <button className="py-1.5 px-3 rounded-md text-xs cursor-pointer transition-all bg-blue-500 text-white border-none hover:bg-blue-600 max-md:flex-1" onClick={applyFilters}>
                적용
              </button>
              <button className="py-1.5 px-3 rounded-md text-xs cursor-pointer transition-all bg-white border border-gray-200 text-gray-600 hover:border-slate-300 hover:text-gray-900 max-md:flex-1" onClick={resetFilters}>
                초기화
              </button>
            </div>
          </div>
        </div>

        {/* 로그 목록 */}
        <div className="flex-1 overflow-y-auto py-4 px-6 min-h-[300px] max-h-[calc(90vh-350px)] max-md:max-h-[calc(100vh-380px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-gray-600">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <span>로딩 중...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-gray-600">
              <span className="text-3xl">⚠️</span>
              <span>{error}</span>
              <button className="py-2 px-4 bg-blue-500 text-white border-none rounded-md cursor-pointer" onClick={() => loadLogs(pagination.page)}>다시 시도</button>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-gray-600">
              <span className="text-3xl">📭</span>
              <span>활동 로그가 없습니다</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {logs.map(log => {
                const statusInfo = getStatusInfo(log.status);
                const targetLink = getTargetLink(log.target_type, log.target_id, log.service);
                return (
                  <div key={log.id} className="p-3 px-4 bg-slate-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[11px] font-semibold uppercase py-0.5 px-1.5 bg-gray-200 rounded text-slate-600">
                        {getCategoryLabel(log.category)}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {getActionTypeLabel(log.action_type)}
                      </span>
                      <span
                        className="text-[11px] py-0.5 px-2 rounded-full ml-auto"
                        style={{
                          color: statusInfo.color,
                          backgroundColor: statusInfo.bgColor
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5 text-[13px] text-gray-600 flex-wrap max-md:flex-col max-md:items-start max-md:gap-2">
                      <span className="py-0.5 px-2 bg-blue-100 rounded text-blue-800 text-[11px] font-medium flex-shrink-0">
                        {getServiceLabel(log.service)}
                      </span>
                      {log.target_type && (
                        <div className="flex items-center gap-2 flex-wrap min-w-0 max-md:w-full">
                          <span className="text-slate-500 text-xs break-all">
                            {getTargetTypeLabel(log.target_type)}: {log.target_id}
                          </span>
                          {targetLink && (
                            <a
                              href={targetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] py-0.5 px-2.5 bg-blue-500 text-white rounded no-underline transition-all whitespace-nowrap flex-shrink-0 hover:bg-blue-600"
                              title={`${getTargetTypeLabel(log.target_type)} 보기`}
                            >
                              바로가기 →
                            </a>
                          )}
                        </div>
                      )}
                      {log.exp_granted > 0 && (
                        <span className="text-emerald-600 font-medium">+{log.exp_granted} XP</span>
                      )}
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-dashed border-gray-200">
                        {Object.entries(log.details).map(([key, value]) => (
                          <span key={key} className="text-xs text-slate-500 bg-slate-100 py-0.5 px-1.5 rounded">
                            {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span className="cursor-help" title={formatDate(log.created_at)}>
                        {getRelativeTime(log.created_at)}
                      </span>
                      {log.ip_address && (
                        <span className="font-mono">IP: {log.ip_address}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 py-4 px-6 border-t border-gray-200">
            <button
              disabled={pagination.page <= 1}
              onClick={() => loadLogs(pagination.page - 1)}
              className="py-2 px-4 bg-slate-50 border border-gray-200 rounded-md cursor-pointer text-[13px] text-gray-900 transition-all hover:enabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-[13px] text-gray-600">
              {pagination.page} / {pagination.totalPages}
              <span className="ml-2 text-slate-400">({pagination.total}건)</span>
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => loadLogs(pagination.page + 1)}
              className="py-2 px-4 bg-slate-50 border border-gray-200 rounded-md cursor-pointer text-[13px] text-gray-900 transition-all hover:enabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivityModal;
